import { useMemo, useState, type ReactNode } from "react";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { PrimaryButton, SecondaryButton } from "@/components/common";
import { CannabisIcon } from "@/components/icons/CannabisIcon";
import { useSessionContext } from "@/contexts/SessionContext";

import {
  PATIENT_SUPPORTING_DOCUMENT_TYPES,
  PatientSupportingDocumentRecord,
  PatientSupportingDocumentTypeConfig,
  PatientSupportingDocumentTypeId,
} from "./types";
import { usePatientSupportingDocuments } from "./usePatientSupportingDocuments";
import { PatientSupportingDocumentCard } from "./PatientSupportingDocumentCard";
import { PatientSupportingDocumentDialog } from "./PatientSupportingDocumentDialog";
import { PatientSupportingDocumentPreviewDialog } from "./PatientSupportingDocumentPreviewDialog";
import { PatientSupportingDocumentHistoryDialog } from "./PatientSupportingDocumentHistoryDialog";
import { CannabinoidMarketplacePanel } from "./CannabinoidMarketplacePanel";
import {
  CannabinoidSortableSection,
  DocumentsProgressBadge,
} from "./CannabinoidSortableSection";
import {
  useCannabinoidSectionsLayout,
  type CannabinoidSectionId,
} from "./useCannabinoidSectionsLayout";

interface PatientSupportingDocumentsPanelProps {
  userId?: string;
  compact?: boolean;
  showHeader?: boolean;
  /** page = tela dedicada com grid mais espaçado */
  layout?: "default" | "page";
}

export function PatientSupportingDocumentsPanel({
  userId,
  compact = false,
  showHeader = true,
  layout = "default",
}: PatientSupportingDocumentsPanelProps) {
  const theme = useTheme();
  const { user, userPatient } = useSessionContext();
  const {
    documents,
    cancelledDocuments,
    isHydrated,
    uploadedCount,
    totalCount,
    isDocumentsComplete,
    saveDocument,
    removeDocument,
  } = usePatientSupportingDocuments(userId);

  const [activeType, setActiveType] =
    useState<PatientSupportingDocumentTypeConfig | null>(null);
  const [activeDocument, setActiveDocument] =
    useState<PatientSupportingDocumentRecord | null>(null);
  const [documentToRemove, setDocumentToRemove] = useState<{
    typeId: PatientSupportingDocumentTypeId;
    document: PatientSupportingDocumentRecord;
  } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [previewDocument, setPreviewDocument] =
    useState<PatientSupportingDocumentRecord | null>(null);
  const [historyType, setHistoryType] =
    useState<PatientSupportingDocumentTypeConfig | null>(null);
  const [marketplaceHeaderActions, setMarketplaceHeaderActions] =
    useState<ReactNode>(null);

  const sectionsLayout = useCannabinoidSectionsLayout();

  const patientId = user?.username || userId || "";
  const userEmail = user?.email?.[0] ?? "";
  const isPageLayout = layout === "page";

  const progress = useMemo(() => {
    if (!totalCount) return 0;
    return Math.round((uploadedCount / totalCount) * 100);
  }, [uploadedCount, totalCount]);

  const openDialog = (
    config: PatientSupportingDocumentTypeConfig,
    document: PatientSupportingDocumentRecord | null = null,
  ) => {
    setActiveDocument(document);
    setActiveType(config);
  };

  const closeDocumentDialog = () => {
    setActiveType(null);
    setActiveDocument(null);
  };

  const handleRemoveDocument = async () => {
    if (!documentToRemove || isRemoving) return;

    setIsRemoving(true);
    try {
      const removed = await removeDocument(
        documentToRemove.typeId,
        documentToRemove.document,
      );
      if (removed) setDocumentToRemove(null);
    } finally {
      setIsRemoving(false);
    }
  };

  const documentsGrid = (
    <Box
      sx={{
        display: "grid",
        width: "100%",
        alignItems: "stretch",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: "repeat(2, minmax(0, 1fr))",
        },
        gap: compact ? 1.25 : isPageLayout ? 1.5 : 1.5,
      }}
    >
      {PATIENT_SUPPORTING_DOCUMENT_TYPES.map((config) => (
        <PatientSupportingDocumentCard
          key={config.id}
          config={config}
          documents={documents[config.id] ?? []}
          cancelledCount={cancelledDocuments[config.id]?.length ?? 0}
          compact={compact && !isPageLayout}
          pageLayout={isPageLayout}
          onAdd={() => openDialog(config)}
          onReplace={(document) => openDialog(config, document)}
          onRemove={(document) =>
            setDocumentToRemove({ typeId: config.id, document })
          }
          onView={(document) => setPreviewDocument(document)}
          onOpenHistory={() => setHistoryType(config)}
        />
      ))}
    </Box>
  );

  const renderMarketplaceSectionContent = () => (
    <CannabinoidMarketplacePanel
      userEmail={userEmail}
      patientId={patientId}
      userPatient={userPatient}
      userName={user?.name}
      hideHeader
      canBrowseStores={isDocumentsComplete}
      documentsPendingCount={totalCount - uploadedCount}
      onHeaderActionsChange={setMarketplaceHeaderActions}
    />
  );

  const renderPageSection = (sectionId: CannabinoidSectionId) => {
    if (sectionId === "documents") {
      return (
        <CannabinoidSortableSection
          key={sectionId}
          id={sectionId}
          title="Documentos"
          subtitle="Envie, edite ou remova os arquivos do tratamento canabinoide."
          icon={<DescriptionOutlinedIcon fontSize="small" />}
          badge={
            <DocumentsProgressBadge
              uploadedCount={uploadedCount}
              totalCount={totalCount}
            />
          }
          isCollapsed={sectionsLayout.collapsedMap.documents}
          onToggleCollapsed={() => sectionsLayout.toggleCollapsed("documents")}
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="body2" fontWeight={700} color="primary800.main">
                {uploadedCount} de {totalCount} enviados
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {progress}% concluído
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 99,
                bgcolor: alpha(theme.palette.primary800.main, 0.08),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 99,
                  bgcolor: "primary800.main",
                },
              }}
            />
            {documentsGrid}
          </Stack>
        </CannabinoidSortableSection>
      );
    }

    return (
      <CannabinoidSortableSection
        key={sectionId}
        id={sectionId}
        title="Dispensação"
        subtitle="Solicite produtos canabinoides nos dispensadores parceiros."
        icon={<LocalPharmacyOutlinedIcon fontSize="small" />}
        badge={
          isDocumentsComplete ? (
            <Chip
              label="Disponível"
              size="small"
              color="success"
              sx={{ fontWeight: 700, height: 24, fontSize: "0.8125rem" }}
            />
          ) : (
            <Chip
              label="Documentos pendentes"
              size="small"
              color="warning"
              sx={{ fontWeight: 700, height: 24, fontSize: "0.8125rem" }}
            />
          )
        }
        headerActions={marketplaceHeaderActions}
        isCollapsed={sectionsLayout.collapsedMap.marketplace}
        onToggleCollapsed={() => sectionsLayout.toggleCollapsed("marketplace")}
      >
        {renderMarketplaceSectionContent()}
      </CannabinoidSortableSection>
    );
  };

  if (!isHydrated) {
    return (
      <Box sx={{ py: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={compact || isPageLayout ? 2 : 2}>
      {showHeader ? (
        <Box sx={{ px: compact ? 0 : 0.5, py: compact ? 0 : 0.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.primary800.main, 0.12),
                  color: "primary800.main",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <CannabisIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Tratamento canabinoide
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Envie, edite ou remova documentos usados em agendamentos e
                  solicitações.
                </Typography>
              </Box>
            </Stack>

            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 99,
                bgcolor: alpha(theme.palette.primary800.main, 0.08),
                color: "primary800.main",
                whiteSpace: "nowrap",
              }}
            >
              {uploadedCount} de {totalCount} enviados
            </Typography>
          </Stack>

          <Box sx={{ mt: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 99,
                bgcolor: alpha(theme.palette.primary800.main, 0.08),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 99,
                  bgcolor: "primary800.main",
                },
              }}
            />
          </Box>
        </Box>
      ) : null}

      {isPageLayout ? (
        <DndContext
          sensors={sectionsLayout.sensors}
          collisionDetection={sectionsLayout.collisionDetection}
          onDragEnd={sectionsLayout.handleDragEnd}
        >
          <SortableContext
            items={sectionsLayout.sectionOrder}
            strategy={sectionsLayout.sortingStrategy}
          >
            <Stack spacing={2.5} divider={<Divider flexItem sx={{ opacity: 0.6 }} />}>
              {sectionsLayout.sectionOrder.map(renderPageSection)}
            </Stack>
          </SortableContext>
        </DndContext>
      ) : (
        documentsGrid
      )}

      {activeType ? (
        <PatientSupportingDocumentDialog
          open={!!activeType}
          onClose={closeDocumentDialog}
          config={activeType}
          existing={activeDocument ?? undefined}
          onSave={async ({ file, notes, description }) => {
            return saveDocument({
              typeId: activeType.id,
              file,
              notes,
              description,
              existingDocument: activeDocument ?? undefined,
            });
          }}
        />
      ) : null}

      <PatientSupportingDocumentPreviewDialog
        open={!!previewDocument}
        onClose={() => setPreviewDocument(null)}
        document={previewDocument}
      />

      <PatientSupportingDocumentHistoryDialog
        open={!!historyType}
        onClose={() => setHistoryType(null)}
        typeTitle={historyType?.title}
        activeDocuments={
          historyType ? documents[historyType.id] ?? [] : []
        }
        cancelledDocuments={
          historyType ? cancelledDocuments[historyType.id] ?? [] : []
        }
        onView={(document) => {
          setPreviewDocument(document);
        }}
      />

      <Dialog
        open={!!documentToRemove}
        onClose={() => {
          if (!isRemoving) setDocumentToRemove(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Remover documento?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            O arquivo {documentToRemove?.document.fileName ?? "selecionado"} será removido.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 1, px: 3, pb: 2 }}>
          <SecondaryButton
            width="fit-content"
            height="40px"
            onClick={() => setDocumentToRemove(null)}
            disabled={isRemoving}
          >
            Cancelar
          </SecondaryButton>
          <Button
            variant="contained"
            onClick={handleRemoveDocument}
            disabled={isRemoving}
            sx={{
              height: "40px",
              textTransform: "none",
              fontWeight: 700,
              color: "neutral0.main",
              bgcolor: "error700.main",
              "&:hover": { bgcolor: "error800.main" },
            }}
          >
            {isRemoving ? "Removendo..." : "Remover"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
